// client/src/components/admin/PlayoffConfirmationModal.js
import React, { useState } from 'react'
import ConfirmationModal from '../common/ConfirmationModal'
import './PlayoffConfirmationModal.css'
import { MINIMUM_MATCHES_FOR_ELIGIBILITY } from '../../utils/pointsCalculation'

function PlayoffConfirmationModal({
  isOpen,
  onClose,
  onConfirm,
  leagueName,
  isSubmitting,
  bracketInfo,
  eligibilityInfo,
  onSkipPlayoffs,
}) {
  const [showSkipConfirmModal, setShowSkipConfirmModal] = useState(false)
  const isPerLevelEligibility = Array.isArray(eligibilityInfo)
  const levelEligibilityInfo = isPerLevelEligibility ? eligibilityInfo : []
  const perLevelCanStart = isPerLevelEligibility
    ? levelEligibilityInfo.some(
        (e) => (e.eligibility?.eligible_for_playoffs ?? 0) > 0,
      )
    : null

  if (!isOpen) return null

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose()
    }
  }

  const handleConfirm = () => {
    onConfirm()
  }

  const handleSkipClick = () => {
    setShowSkipConfirmModal(true)
  }

  const handleSkipConfirm = () => {
    setShowSkipConfirmModal(false)
    onSkipPlayoffs()
  }

  // Helper to get bracket description text
  const getBracketDescription = () => {
    // Prefer bracketInfo (actual generated bracket) over eligibility (preview)
    if (bracketInfo) {
      const byeText =
        bracketInfo.numByes > 0
          ? ` (${bracketInfo.numByes} bye${bracketInfo.numByes !== 1 ? 's' : ''})`
          : ''
      return `${bracketInfo.participantsCount} players in ${bracketInfo.bracketSize}-player bracket${byeText}`
    }
    if (eligibilityInfo && eligibilityInfo.eligible_for_playoffs > 0) {
      return `${eligibilityInfo.eligible_for_playoffs} player${eligibilityInfo.eligible_for_playoffs !== 1 ? 's' : ''} will compete`
    }
    if (isPerLevelEligibility) {
      const totalEligible = levelEligibilityInfo.reduce(
        (sum, entry) => sum + (entry.eligibility?.eligible_for_playoffs || 0),
        0,
      )
      return `${totalEligible} player${totalEligible !== 1 ? 's' : ''} across all levels will compete`
    }
    return 'A tournament bracket will be generated'
  }

  const renderEligibilitySection = (sectionEligibility) => {
    const hasSectionIneligibleDivisions = sectionEligibility?.divisions?.some(
      (d) => d.total_players > 0 && d.eligible_players === 0,
    )
    const hasSectionSinglePlayerDivisions = sectionEligibility?.divisions?.some(
      (d) => d.total_players === 1,
    )
    const sectionMinimumMatchesRequired =
      sectionEligibility?.minimum_matches_required ??
      MINIMUM_MATCHES_FOR_ELIGIBILITY

    return (
      <>
        <div className="table-responsive">
          <table className="table table-sm">
            <thead>
              <tr>
                <th>Division</th>
                <th>Total Players</th>
                <th>Eligible</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {sectionEligibility.divisions.map((division) => (
                <tr key={division.division_id}>
                  <td>{division.division_name}</td>
                  <td>{division.total_players}</td>
                  <td>{division.eligible_players}</td>
                  <td>
                    {division.eligible_players === 0 ? (
                      <span className="badge bg-warning text-dark">
                        No participants
                      </span>
                    ) : division.total_players === 1 ? (
                      <span className="badge bg-info">Single player</span>
                    ) : division.ineligible_players > 0 ? (
                      <span className="badge bg-secondary">
                        Partial ({division.eligible_players}/
                        {division.total_players})
                      </span>
                    ) : (
                      <span className="badge bg-success">All eligible</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {(hasSectionIneligibleDivisions || hasSectionSinglePlayerDivisions) && (
          <div className="alert alert-warning mt-2">
            <small>
              <strong>⚠️ Note:</strong>
              {hasSectionIneligibleDivisions && (
                <span>
                  {' '}
                  Some divisions have no eligible players (players need{' '}
                  {sectionMinimumMatchesRequired}+ completed matches to
                  qualify).{' '}
                </span>
              )}
              {hasSectionSinglePlayerDivisions && (
                <span>
                  {' '}
                  Single-player divisions cannot participate in playoffs (no
                  opponents for matches).{' '}
                </span>
              )}
              Playoffs will proceed with eligible players only.
            </small>
          </div>
        )}

        {sectionEligibility.ineligible_for_playoffs > 0 && (
          <div className="alert alert-info mt-2">
            <small>
              <strong>ℹ️ Summary:</strong>{' '}
              {sectionEligibility.eligible_for_playoffs} of{' '}
              {sectionEligibility.total_registered_players} registered players
              are eligible for playoffs.
              {sectionEligibility.ineligible_for_playoffs} player(s) excluded
              due to insufficient matches (minimum{' '}
              {sectionMinimumMatchesRequired} required).
            </small>
          </div>
        )}
      </>
    )
  }

  return (
    <>
      <div className="playoff-modal-backdrop" onClick={handleBackdropClick}>
        <div
          className="playoff-modal"
          role="dialog"
          aria-modal="true"
          aria-labelledby="playoff-modal-title"
        >
          <div className="playoff-modal-header">
            <h3 id="playoff-modal-title">🏆 Start Playoffs</h3>
            <button
              className="playoff-modal-close"
              onClick={onClose}
              aria-label="Close"
              disabled={isSubmitting}
            >
              ×
            </button>
          </div>

          <div className="playoff-modal-body">
            <div className="alert alert-warning">
              <strong>⚠️ This action cannot be undone!</strong>
            </div>

            <p className="mb-3">
              Starting playoffs for <strong>{leagueName}</strong> will:
            </p>

            <ul className="playoff-effects-list">
              <li>
                🔒 <strong>Lock standings</strong> - Final regular season
                rankings will be frozen
              </li>
              <li>
                🏁 <strong>Generate seeds</strong> - Players will be seeded
                based on current standings
              </li>
              <li>
                🗂️ <strong>Create bracket</strong> - {getBracketDescription()}
              </li>
              <li>
                📋 <strong>Record audit entry</strong> - This action will be
                logged with your admin user and timestamp
              </li>
            </ul>

            {/* Show eligibility information if available */}
            {eligibilityInfo && (
              <div className="mt-3">
                <h5>Playoff Participation</h5>
                {isPerLevelEligibility
                  ? levelEligibilityInfo.map((entry) => (
                      <details key={entry.level.id} className="mb-2" open>
                        <summary>
                          <strong>Level {entry.level.skill_level}</strong>
                        </summary>
                        <div className="mt-2">
                          {renderEligibilitySection(entry.eligibility)}
                        </div>
                      </details>
                    ))
                  : renderEligibilitySection(eligibilityInfo)}
              </div>
            )}

            <div className="alert alert-info mt-3">
              <small>
                <strong>Note:</strong> Make sure all regular season matches are
                completed before starting playoffs.
              </small>
            </div>
          </div>

          <div className="playoff-modal-footer">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={onClose}
              disabled={isSubmitting}
            >
              Cancel
            </button>
            {((!isPerLevelEligibility &&
              eligibilityInfo &&
              !eligibilityInfo.can_start_playoffs) ||
              (isPerLevelEligibility && perLevelCanStart === false)) && (
              <button
                type="button"
                className="btn btn-warning"
                onClick={handleSkipClick}
                disabled={isSubmitting}
                title="Skip playoffs and mark league as completed"
              >
                {isSubmitting ? 'Skipping...' : 'Skip Playoffs'}
              </button>
            )}
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleConfirm}
              disabled={
                isSubmitting ||
                (!isPerLevelEligibility &&
                  eligibilityInfo &&
                  !eligibilityInfo.can_start_playoffs) ||
                (isPerLevelEligibility && perLevelCanStart === false)
              }
              title={
                !isPerLevelEligibility &&
                eligibilityInfo &&
                !eligibilityInfo.can_start_playoffs
                  ? eligibilityInfo.ineligibility_reason
                  : ''
              }
            >
              {isSubmitting ? 'Starting Playoffs...' : 'Start Playoffs'}
            </button>
          </div>
        </div>
      </div>
      <ConfirmationModal
        isOpen={showSkipConfirmModal}
        title="Skip Playoffs?"
        message={`Are you sure you want to skip playoffs for "${leagueName}"? This will mark the league as completed without running playoffs. This action cannot be undone.`}
        confirmLabel="Yes, Skip Playoffs"
        confirmVariant="warning"
        isSubmitting={isSubmitting}
        onConfirm={handleSkipConfirm}
        onClose={() => setShowSkipConfirmModal(false)}
      />
    </>
  )
}

export default PlayoffConfirmationModal
